import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaGavel, FaClock, FaUsers, FaLock, FaBolt, FaCalendarAlt } from 'react-icons/fa';
// ============================================
// HELPERS
// ============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};
const getTimeRemaining = (endTime) => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const diff = end - now;
    if (diff <= 0)
        return 'Ended';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0)
        return `${days}d ${hours}h`;
    if (hours > 0)
        return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};
const getBadgeInfo = (auction) => {
    if (auction.status === 'closed' || auction.status === 'cancelled') {
        return { label: 'CLOSED', variant: 'closed' };
    }
    if (auction.visibility === 'private') {
        return { label: 'PRIVATE', variant: 'private' };
    }
    if (auction.status === 'scheduled') {
        return { label: 'UPCOMING', variant: 'upcoming' };
    }
    if (auction.auctionType === 'supervised') {
        return { label: 'LIVE SUPERVISED', variant: 'live' };
    }
    return { label: 'AUTOMATED', variant: 'automated' };
};
const getCTALabel = (auction) => {
    if (auction.status === 'closed' || auction.status === 'cancelled') {
        return 'View Results';
    }
    if (auction.status === 'scheduled') {
        return 'Set Reminder';
    }
    if (auction.auctionType === 'supervised' && auction.status === 'active') {
        return 'Join Live Auction';
    }
    return 'View & Bid';
};
// ============================================
// COMPONENT
// ============================================
const AuctionCard = ({ auction, onClick }) => {
    const { diamondSnapshot } = auction;
    const badge = useMemo(() => getBadgeInfo(auction), [auction]);
    const timeLeft = useMemo(() => getTimeRemaining(auction.scheduledEndTime), [auction.scheduledEndTime]);
    const ctaLabel = useMemo(() => getCTALabel(auction), [auction]);
    const isActive = auction.status === 'active' || auction.status === 'grace_period';
    const isLiveNow = auction.auctionType === 'supervised' && isActive;
    return (_jsxs(Card, { onClick: onClick, children: [_jsxs(ImageArea, { children: [_jsxs(DiamondPlaceholder, { children: [_jsx("span", { children: diamondSnapshot.shape }), _jsxs("small", { children: [diamondSnapshot.caratWeight.toFixed(2), " ct"] })] }), _jsxs(TypeBadge, { "$variant": badge.variant, children: [badge.variant === 'live' && _jsx(FaBolt, {}), badge.variant === 'private' && _jsx(FaLock, {}), badge.variant === 'upcoming' && _jsx(FaCalendarAlt, {}), badge.label] }), diamondSnapshot.lab && (_jsx(LabBadge, { children: diamondSnapshot.lab }))] }), _jsxs(CardBody, { children: [_jsxs(CardTitle, { children: [diamondSnapshot.caratWeight.toFixed(2), " ct ", diamondSnapshot.shape, diamondSnapshot.color && ` ${diamondSnapshot.color}`, diamondSnapshot.clarity && ` ${diamondSnapshot.clarity}`] }), _jsxs(SpecRow, { children: [diamondSnapshot.color && _jsxs(SpecTag, { children: ["Color: ", diamondSnapshot.color] }), diamondSnapshot.clarity && _jsxs(SpecTag, { children: ["Clarity: ", diamondSnapshot.clarity] }), diamondSnapshot.certificateNumber && (_jsxs(SpecTag, { children: ["Cert: ", diamondSnapshot.certificateNumber] }))] }), _jsxs(PriceSection, { children: [_jsx(PriceLabel, { children: isActive ? 'Current Bid' : auction.status === 'scheduled' ? 'Starting At' : 'Final Price' }), _jsx(PriceValue, { children: formatCurrency(isActive ? auction.currentBid : auction.status === 'scheduled' ? auction.startingPrice : (auction.finalPrice || auction.currentBid)) })] }), _jsxs(MetaRow, { children: [_jsxs(MetaItem, { children: [_jsx(FaUsers, {}), _jsxs("span", { children: [auction.bidCount, " bid", auction.bidCount !== 1 ? 's' : ''] })] }), _jsx(MetaItem, { "$highlight": isLiveNow, children: isLiveNow ? (_jsxs(_Fragment, { children: [_jsx(LiveDot, {}), _jsx("span", { children: "Live Now" })] })) : (_jsxs(_Fragment, { children: [_jsx(FaClock, {}), _jsx("span", { children: timeLeft })] })) })] }), _jsxs(CTAButton, { "$variant": badge.variant, onClick: (e) => { e.stopPropagation(); onClick(); }, children: [_jsx(FaGavel, {}), ctaLabel] })] })] }));
};
export default AuctionCard;
// ============================================
// STYLED COMPONENTS
// ============================================
const Card = styled.div `
  background: var(--bg-primary, #1a1a2e);
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #f0d48a));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    border-color: var(--accent-gold-border, rgba(230, 192, 104, 0.25));
  }

  &:hover::after {
    opacity: 1;
  }
`;
const ImageArea = styled.div `
  position: relative;
  height: 180px;
  background: linear-gradient(135deg, var(--bg-secondary, #16213e) 0%, var(--bg-tertiary, #0f3460) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const DiamondPlaceholder = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: var(--text-secondary, #a0a0b0);
  transition: transform 0.3s;

  ${Card}:hover & {
    transform: scale(1.05) rotate(2deg);
  }

  span {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--text-primary, #f0f0f0);
  }

  small {
    font-size: 13px;
    color: var(--accent-gold, #e6c068);
    font-weight: 500;
  }
`;
const badgeColors = {
    live: '#dc2626',
    automated: '#16a34a',
    upcoming: '#2563eb',
    private: '#d97706',
    closed: '#6b7280',
};
const TypeBadge = styled.span `
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => badgeColors[props.$variant]};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;

  svg {
    width: 10px;
    height: 10px;
  }
`;
const LabBadge = styled.span `
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  z-index: 2;
`;
const CardBody = styled.div `
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const CardTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SpecRow = styled.div `
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const SpecTag = styled.span `
  background: var(--bg-secondary, #16213e);
  padding: 3px 8px;
  font-size: 11px;
  color: var(--text-secondary, #a0a0b0);
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  border-radius: 4px;
  font-weight: 500;
`;
const PriceSection = styled.div `
  padding: 10px 0 4px;
  border-top: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;
const PriceLabel = styled.div `
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-tertiary, #6a6a7a);
  margin-bottom: 2px;
  font-weight: 600;
`;
const PriceValue = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 24px;
  font-weight: 600;
  color: var(--accent-gold, #e6c068);
`;
const MetaRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const MetaItem = styled.div `
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${props => props.$highlight ? '#ef4444' : 'var(--text-secondary, #a0a0b0)'};
  font-weight: ${props => props.$highlight ? '600' : '500'};

  svg {
    width: 12px;
    height: 12px;
  }
`;
const pulse = keyframes `
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;
const LiveDot = styled.span `
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 1.5s ease-in-out infinite;
  flex-shrink: 0;
`;
const ctaVariantStyles = {
    live: css `
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    border-color: #dc2626;
    &:hover {
      background: linear-gradient(135deg, #ef4444, #dc2626);
      box-shadow: 0 4px 16px rgba(220, 38, 38, 0.35);
    }
  `,
    automated: css `
    background: linear-gradient(135deg, var(--accent-gold, #e6c068), var(--accent-gold-light, #f0d48a));
    border-color: var(--accent-gold, #e6c068);
    color: #1a1a2e;
    &:hover {
      box-shadow: 0 4px 16px rgba(230, 192, 104, 0.35);
    }
  `,
    upcoming: css `
    background: transparent;
    border-color: var(--accent-gold, #e6c068);
    color: var(--accent-gold, #e6c068);
    &:hover {
      background: var(--accent-gold-bg, rgba(230, 192, 104, 0.08));
      box-shadow: 0 4px 16px rgba(230, 192, 104, 0.15);
    }
  `,
    private: css `
    background: linear-gradient(135deg, #d97706, #b45309);
    border-color: #d97706;
    &:hover {
      background: linear-gradient(135deg, #f59e0b, #d97706);
      box-shadow: 0 4px 16px rgba(217, 119, 6, 0.35);
    }
  `,
    closed: css `
    background: transparent;
    border-color: var(--border-color, rgba(255, 255, 255, 0.08));
    color: var(--text-secondary, #a0a0b0);
    &:hover {
      border-color: var(--text-secondary, #a0a0b0);
      background: var(--bg-secondary, #16213e);
    }
  `,
};
const CTAButton = styled.button `
  width: 100%;
  padding: 11px 16px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    transform: translateY(-1px);
  }

  ${props => ctaVariantStyles[props.$variant]}
`;
